/* eslint-disable no-await-in-loop */
import {
    EntitiesRequestMessage,
    EntitiesResponseMessage,
    EntitiesTrainingMessage,
} from 'api/api';
import { wsEndpoint } from 'api/ApiConfig';
import { IAuthModel } from 'utilities/auth';

class ReqterSDK {
    private ws: WebSocket | null = null;

    private pushError: ((text: string) => void) | undefined;

    private subscribeToTrainingConversationCallback:
        | ((trainingMessage?: EntitiesTrainingMessage) => void)
        | undefined;

    private sendQueue: EntitiesRequestMessage[] = [];

    private eventQueue: EntitiesResponseMessage[] = [];

    private isProcessing = false;

    private isConnected = false;

    private connectionCallbacks: (() => void)[] = [];

    close() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.close();
    }

    isConnecting() {
        return this.ws && this.ws.readyState === WebSocket.CONNECTING;
    }

    connect(pushError: (text: string) => void): void {
        if (
            this.ws?.readyState === WebSocket.CONNECTING ||
            this.ws?.readyState === WebSocket.OPEN
        ) {
            return;
        }

        console.log('Connecting to WebSocket server');
        const sessionAuth = localStorage.getItem('auth');
        const sessionAuthModel =
            sessionAuth && sessionAuth.length > 0
                ? (JSON.parse(sessionAuth) as IAuthModel)
                : undefined;

        this.ws = new WebSocket(wsEndpoint());

        this.ws.onopen = () => {
            console.info('Connected to WebSocket server');
            this.isConnected = true;

            const authMsg: string = JSON.stringify({
                token: sessionAuthModel?.token,
            });
            this.ws?.send(authMsg);

            // Process sendQueue
            const queue = this.sendQueue;
            this.sendQueue = [];
            queue.forEach((q) => {
                this.sendMessage(q);
            });

            // Notify connection callbacks
            this.connectionCallbacks.forEach((callback) => callback());
            this.connectionCallbacks = [];
        };

        this.ws.onmessage = async (event) => {
            console.log('<-', event.data);
            this.eventQueue.push(
                JSON.parse(event.data) as EntitiesResponseMessage
            );
            this.processQueue();
        };

        this.ws.onclose = () => {
            console.log('Disconnected from WebSocket server');
            this.ws = null;
            this.isConnected = false;
            setTimeout(() => {
                this.connect(pushError);
            }, 500);
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error', error);
            this.ws?.close();
            this.ws = null;
            this.isConnected = false;
            setTimeout(() => {
                this.connect(pushError);
            }, 500);
        };
    }

    sendMessage(data: EntitiesRequestMessage) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            console.log('->', data);
            this.ws?.send(JSON.stringify(data));
        } else {
            this.sendQueue.push(data);
        }
    }

    async waitForConnection(): Promise<void> {
        if (this.isConnected) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.connectionCallbacks.push(resolve);
        });
    }

    async subscribeToTrainingConversation(
        courseID: number,
        topic: string,
        subtopic: string,
        callback: (trainingMessage?: EntitiesTrainingMessage) => void
    ) {
        // Wait for the WebSocket connection to be established
        await this.waitForConnection();

        this.subscribeToTrainingConversationCallback = callback;
        this.sendMessage({
            subscribeToTrainingConversation: { courseID, topic, subtopic },
        });
    }

    private async processQueue(): Promise<void> {
        if (this.isProcessing) {
            return; // Exit if already processing
        }

        this.isProcessing = true;

        // Loop through the queue and process events sequentially
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift(); // Get the first event
            if (event) {
                await this.processMessage(event); // Await the event to finish before processing the next one
            }
        }

        this.isProcessing = false;
    }

    async processMessage(data: EntitiesResponseMessage): Promise<void> {
        if (data.trainingMessage !== undefined) {
            // Handle training message
            if (this.subscribeToTrainingConversationCallback !== undefined) {
                this.subscribeToTrainingConversationCallback(
                    data.trainingMessage
                );
            }
        } else if (data.error !== undefined && this.pushError !== undefined) {
            this.pushError(data.error.message || '');
        }
    }
}

export const Reqter = new ReqterSDK();
